import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  useDashboard,
  useDeleteDashboard,
  useRefreshDashboard,
  useSchedules,
  useSetDashboardLayout,
  useUpdateDashboard,
  type Tile,
  useDashboardTrend,
  useIsAdmin,
} from "@/api/queries";
import { AddTileDialog } from "@/components/reports/AddTileDialog";
import { AlertsDialog } from "@/components/reports/AlertsDialog";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { ScheduleDialog } from "@/components/reports/ScheduleDialog";
import { ShareDialog } from "@/components/reports/ShareDialog";
import { TileGrid } from "@/components/reports/TileGrid";
import { TileSqlDialog, TileSqlViewer } from "@/components/reports/TileSqlDialog";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EditableTitle } from "@/components/ui/EditableTitle";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import {
  AlertIcon,
  BackIcon,
  BellIcon,
  CalendarIcon,
  DashboardIcon,
  DatabaseIcon,
  LinkIcon,
  PlusIcon,
  RefreshIcon,
  TrashIcon,
} from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/**
 * One dashboard (PRD FR-7.3).
 *
 * Unlike a report it does **not** refresh on open: a dashboard can span
 * several databases and a dozen tiles, and running all of that because someone
 * clicked a link is a decision, not a default. Refresh All is one button, and
 * the response says what it actually did — how many queries served how many
 * tiles, which is the dedup made visible.
 */
export function DashboardPage() {
  const { dashboardId } = useParams({ from: "/app/dashboards/$dashboardId" });
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: dashboard, isLoading } = useDashboard(dashboardId);
  const refresh = useRefreshDashboard(dashboardId);
  const layout = useSetDashboardLayout(dashboardId);
  const remove = useDeleteDashboard();
  const rename = useUpdateDashboard(dashboardId);
  const stamp = useRelativeTime(dashboard?.last_refreshed_at);

  const [editing, setEditing] = useState<Tile | null>(null);
  const [viewing, setViewing] = useState<Tile | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const { data: trend, isLoading: trendLoading } = useDashboardTrend(dashboardId, alerting);
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { data: schedules } = useSchedules();
  const schedule = useMemo(
    () =>
      (schedules ?? []).find(
        (entry) => entry.owner_kind === "dashboard" && entry.owner_id === dashboardId,
      ) ?? null,
    [schedules, dashboardId],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (!dashboard) {
    return (
      <div className="px-6 py-10">
        <EmptyState
          icon={<DashboardIcon className="size-6" />}
          title="No such dashboard"
          detail="It may have been deleted."
          action={
            <Link to="/dashboards" className="text-accent hover:underline">
              Back to dashboards
            </Link>
          }
        />
      </div>
    );
  }

  const failures = dashboard.tiles.filter(
    (tile) => tile.refresh_status === "error" || tile.refresh_status === "blocked",
  );

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[100rem] flex-col">
      <header className="border-border flex flex-col gap-3 border-b px-6 py-4">
        <div className="flex flex-wrap items-start gap-3">
          <Link
            to="/dashboards"
            className="text-ink-secondary hover:text-ink hover:bg-hover rounded-element motion-fast mt-0.5 inline-flex size-8 items-center justify-center"
            aria-label="Back to dashboards"
          >
            <BackIcon className="size-4" />
          </Link>
          {/* A floor, not `min-w-0`: the action row is allowed to wrap (the
              header is `flex-wrap`) but the name is not allowed to shrink to
              nothing. At `min-w-0` this title truncated to about eight
              characters on a 1280px window, and the rename field inherited the
              same 121px. */}
          <div className="min-w-56 flex-1">
            <EditableTitle
              as="h1"
              className="type-h2 text-ink"
              value={dashboard.name}
              placeholder="Untitled dashboard"
              label="dashboard"
              onSave={(name) => rename.mutateAsync({ name })}
            />
            {dashboard.description ? (
              <p className="type-support text-ink-secondary mt-1">{dashboard.description}</p>
            ) : null}
          </div>
          <Button
            variant="primary"
            icon={<PlusIcon className="size-4" />}
            onClick={() => setAdding(true)}
          >
            Add tile
          </Button>
          <Button
            icon={<RefreshIcon className="size-4" />}
            loading={refresh.isPending}
            disabled={!dashboard.tiles.some((tile) => tile.query_backed)}
            onClick={() => refresh.mutate()}
          >
            Refresh all
          </Button>
          {/* Who gets emailed what is the Admin tier's (ACC-2). */}
          {isAdmin ? (
            <Button icon={<CalendarIcon className="size-4" />} onClick={() => setScheduling(true)}>
              {schedule ? "Schedule…" : "Schedule"}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button icon={<BellIcon className="size-4" />} onClick={() => setAlerting(true)}>
              Alerts
            </Button>
          ) : null}
          <ExportMenu base={`/api/dashboards/${dashboard.id}`} html />
          <Button icon={<LinkIcon className="size-4" />} onClick={() => setSharing(true)}>
            Share
          </Button>
          <Button icon={<TrashIcon className="size-4" />} onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {dashboard.connections.map((connection) => (
            <Chip key={connection} tone="neutral" icon={<DatabaseIcon className="size-3" />}>
              {connection}
            </Chip>
          ))}
          <span className="type-caption text-ink-disabled">
            {refresh.isPending ? "refreshing…" : stamp ? `refreshed ${stamp}` : "never refreshed"}
          </span>
          {dashboard.refresh ? (
            <span className="type-caption text-ink-disabled">
              · {dashboard.refresh.queries} quer
              {dashboard.refresh.queries === 1 ? "y" : "ies"} for {dashboard.refresh.tiles} tiles
              {dashboard.refresh.saved_queries > 0
                ? ` · ${dashboard.refresh.saved_queries} deduplicated`
                : ""}
            </span>
          ) : null}
        </div>

        {failures.length ? (
          <p className="bg-error-subtle text-error type-support rounded-element flex items-start gap-2 px-3 py-2">
            <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>
              {failures.length} tile{failures.length === 1 ? "" : "s"} could not be refreshed. The
              values shown are the last ones that worked.
            </span>
          </p>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {dashboard.tiles.length ? (
          <TileGrid
            tiles={dashboard.tiles}
            links={dashboard.links ?? []}
            noun="dashboard"
            showConnections
            saving={layout.isPending}
            onLayout={(frames) => layout.mutate(frames)}
            onEditSql={setEditing}
            onViewSql={setViewing}
          />
        ) : (
          <EmptyState
            icon={<DashboardIcon className="size-6" />}
            title="This dashboard is empty"
            detail="Add a tile from a statement you write here, use “Add to…” beside any chart in a conversation, or add a whole report from its page."
            action={
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add tile
              </Button>
            }
          />
        )}
      </div>

      <AddTileDialog
        open={adding}
        onClose={() => setAdding(false)}
        owner={{ kind: "dashboard", id: dashboard.id }}
      />
      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        loading={remove.isPending}
        title="Delete this dashboard?"
        description={<>“{dashboard.name || "Untitled dashboard"}” will be deleted.</>}
        consequence={
          <>
            Its {dashboard.tile_count} tile{dashboard.tile_count === 1 ? "" : "s"}, its snapshots
            and any schedule that refreshes it go with it. The reports and conversations those
            tiles were copied from are not touched.
          </>
        }
        onConfirm={() =>
          remove.mutate(dashboard.id, {
            onSuccess: () => {
              setConfirmingDelete(false);
              void navigate({ to: "/dashboards" });
            },
          })
        }
      />
      <TileSqlDialog tile={editing} open={Boolean(editing)} onClose={() => setEditing(null)} />
      <TileSqlViewer tile={viewing} open={Boolean(viewing)} onClose={() => setViewing(null)} />
      {alerting ? (
        <AlertsDialog
          open
          onClose={() => setAlerting(false)}
          subject={{ kind: "dashboard", id: dashboard.id }}
          subjectName={dashboard.name || "Untitled dashboard"}
          series={(trend?.series ?? []).filter((entry) => entry.points.length > 0)}
          seriesLoading={trendLoading}
        />
      ) : null}
      <ShareDialog
        open={sharing}
        onClose={() => setSharing(false)}
        kind="dashboard"
        subjectId={dashboard.id}
        subjectName={dashboard.name || "Untitled dashboard"}
      />
      <ScheduleDialog
        open={scheduling}
        onClose={() => setScheduling(false)}
        ownerKind="dashboard"
        ownerId={dashboard.id}
        schedule={schedule}
      />
    </div>
  );
}
